import { useCallback, useEffect, useState } from 'react';
import { Building2, Check, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import WidgetCard from '../components/WidgetCard';
import { InlineError } from '../components/InlineError';
import type { GroupView, GroupCompany } from '../lib/types';

const facts = (c: GroupCompany) => [c.number, c.country].filter(Boolean).join(' · ');

// The company this workspace is about and its subsidiaries. A company a connected
// system brought as its own is placed in or outside the group by a person.
export default function GroupStructure() {
  const { authFetch, isAdmin } = useAuth();
  const [view, setView] = useState<GroupView | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    authFetch('/knowledge/group')
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Could not load the group')))
      .then(setView)
      .catch(e => setError(e.message));
  }, [authFetch]);
  useEffect(load, [load]);

  const send = async (path: string, method: string, organization: string) => {
    setError(null);
    const res = await authFetch(path, {
      method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ organization }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      setError(body.detail || 'Could not save that');
      return;
    }
    setView(await res.json());
  };

  if (!view || (!view.principal && view.suggestions.length === 0)) return null;

  return (
    <WidgetCard title="Group structure" icon={Building2}>
      {error && <div className="mb-3"><InlineError message={error} /></div>}
      {view.principal ? (
        <div className="text-sm">
          <p className="text-graphite font-medium">{view.principal.name}</p>
          <p className="text-[12px] text-coolslate">{facts(view.principal) || 'This workspace’s company'}</p>
        </div>
      ) : (
        <p className="text-sm text-coolslate">
          The record names more than one company as your own. Say which one this workspace is about.
        </p>
      )}

      {view.members.length > 0 && (
        <ul className="mt-3 border-l-2 border-softgrey pl-3 space-y-2">
          {view.members.map(m => (
            <li key={m.key} className="flex items-start justify-between gap-3 text-sm">
              <div>
                <p className="text-graphite">{m.name}</p>
                <p className="text-[12px] text-coolslate">
                  Subsidiary{m.parent ? ` of ${m.parent}` : ''}{facts(m) ? ` · ${facts(m)}` : ''}
                </p>
              </div>
              <button
                onClick={() => send('/knowledge/group/removed', 'POST', m.key)}
                className="text-[12px] font-medium text-coolslate hover:text-graphite"
              >
                Not in our group
              </button>
            </li>
          ))}
        </ul>
      )}

      {view.suggestions.length > 0 && (
        <div className="mt-4 space-y-2">
          {view.suggestions.map(s => (
            <div key={s.key} className="rounded-lg border border-softgrey px-3 py-2">
              <p className="text-sm text-graphite">
                {view.principal
                  ? <>Is <strong>{s.name}</strong> part of your group?</>
                  : <><strong>{s.name}</strong>{facts(s) ? ` (${facts(s)})` : ''}</>}
              </p>
              <div className="mt-1.5 flex flex-wrap gap-3">
                {view.principal ? (
                  <>
                    <button onClick={() => send('/knowledge/group/confirmed', 'POST', s.key)}
                      className="inline-flex items-center gap-1 text-[13px] font-medium text-brand-dark hover:text-brand">
                      <Check className="w-3.5 h-3.5" /> Yes, a subsidiary
                    </button>
                    <button onClick={() => send('/knowledge/group/rejected', 'POST', s.key)}
                      className="inline-flex items-center gap-1 text-[13px] font-medium text-coolslate hover:text-graphite">
                      <X className="w-3.5 h-3.5" /> No
                    </button>
                  </>
                ) : isAdmin ? (
                  <button onClick={() => send('/knowledge/group/principal', 'PUT', s.key)}
                    className="text-[13px] font-medium text-brand-dark hover:text-brand">
                    This is our company
                  </button>
                ) : (
                  <span className="text-[12px] text-coolslate">An administrator can choose this as the workspace’s company.</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </WidgetCard>
  );
}
